/** Settings → Queues: one per category × language — its time slots, zone, minimum gap, on/off, and the next free slots. */
import { useState } from "react";
import { Link } from "../../app/router";
import type { Category, Queue } from "../../api/types";
import { Icon } from "../../components/Icon";
import { useToast } from "../../components/toast";
import { Badge, Button, EmptyState, Field, Modal, PageHeader, QueryView, Switch } from "../../components/ui";
import { useApi, useMutation } from "../../data/hooks";
import { useCategories, useLanguages, useQueues } from "../../data/queries";
import { dateTime, languageName } from "../../lib/format";
import { browserTimeZone, timeZoneOptions } from "../../lib/tz";
import { categoryName } from "./AccountsScreen";

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const catName = (slug: string, cats: Category[]) => categoryName(slug, cats) ?? slug;

/** Adds a slot keeping the list sorted and unique; returns null when the value isn't HH:MM. */
export function addSlot(slots: string[], value: string): string[] | null {
  const v = value.trim().slice(0, 5);
  if (!HHMM.test(v)) return null;
  return [...new Set([...slots, v])].sort();
}

export function QueuesScreen() {
  const q = useQueues();
  const cats = useCategories().data ?? [];
  const [editing, setEditing] = useState<Queue | "new" | null>(null);
  return (
    <div className="stack-lg">
      <PageHeader back={<Link to="/settings" className="back-link"><Icon name="back" size={18} /> Settings</Link>} title="Queues"
        subtitle="Each queue posts one category in one language at set times."
        actions={<Button variant="primary" icon="plus" onClick={() => setEditing("new")}>New queue</Button>} />
      <QueryView q={q}>{(items) => items.length ? (
        <ul className="cards" aria-label="Queues">
          {items.map((x) => {
            const paused = !x.enabled || !!x.paused_at;
            return (
              <li key={x.id}>
                <article className={`pub tone-${paused ? "attention" : "ok"}`} aria-label={x.name}>
                  <div className="pub-top">
                    <span className="list-title">{x.name}</span>
                    <span className="push">{paused ? <Badge tone="attention">{x.enabled ? "Paused" : "Off"}</Badge> : <Badge tone="ok">Running</Badge>}</span>
                  </div>
                  <p className="muted small">{catName(x.category, cats)} · {languageName(x.language_code)} · {x.timezone} · at least {fmtGap(x.min_gap_minutes)} apart</p>
                  <p className="chips-inline">{x.slots.map((s) => <span key={s} className="slot-chip">{s}</span>)}</p>
                  {x.next_free_slots.length > 0 && <p className="small">Next free: {x.next_free_slots.slice(0, 3).map((t) => dateTime(t)).join(" · ")}</p>}
                  <div className="pub-actions"><Button size="sm" variant="ghost" icon="edit" onClick={() => setEditing(x)}>Edit</Button></div>
                </article>
              </li>
            );
          })}
        </ul>
      ) : <EmptyState icon="calendar" title="No queues yet" action={<Button variant="secondary" onClick={() => setEditing("new")}>Create a queue</Button>}>Scheduling into “next free slots” needs a queue for the account's category and language.</EmptyState>}</QueryView>
      {editing && <QueueSheet queue={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function fmtGap(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.round((min / 60) * 10) / 10;
  return `${h} h`;
}

function QueueSheet({ queue, onClose }: { queue: Queue | null; onClose: () => void }) {
  const api = useApi();
  const toast = useToast();
  const cats = useCategories().data ?? [];
  const langs = useLanguages().data ?? [];
  const [form, setForm] = useState(() => ({
    name: queue?.name ?? "", category: queue?.category ?? "", language_code: queue?.language_code ?? "en",
    timezone: queue?.timezone ?? browserTimeZone(), slots: queue?.slots ?? ["09:00"], min_gap_minutes: queue?.min_gap_minutes ?? 180,
    priority: queue?.priority ?? 50, enabled: queue?.enabled ?? true,
  }));
  const [newSlot, setNewSlot] = useState("");
  const [slotErr, setSlotErr] = useState<string | undefined>();
  const category = form.category || cats[0]?.slug || "";
  const body = { ...form, name: form.name.trim(), category };
  const save = useMutation(() => (queue ? api.updateQueue(queue.id, body) : api.createQueue(body)), {
    invalidate: ["queues", "dashboard"],
    onSuccess: (r) => {
      // QueueView.warnings: e.g. "N already-planned posts keep their times" — worth reading, so it stays until dismissed.
      if (r.warnings.length) toast.show({ tone: "info", sticky: true, text: `${queue ? "Queue saved." : "Queue created."} ${r.warnings.join(" ")}` });
      else toast.ok(queue ? "Queue saved." : "Queue created.");
      onClose();
    },
    onError: (e) => toast.error(e, "Couldn't save"),
  });
  const add = () => {
    const next = addSlot(form.slots, newSlot);
    if (!next) { setSlotErr("Use a 24-hour time like 19:30."); return; }
    if (next.length > 12) { setSlotErr("At most 12 slots a day."); return; }
    setForm({ ...form, slots: next });
    setNewSlot("");
    setSlotErr(undefined);
  };
  const valid = !!body.name && !!category && form.slots.length > 0 && form.min_gap_minutes >= 0;
  return (
    <Modal title={queue ? `Edit ${queue.name}` : "New queue"} variant="sheet" onClose={onClose}
      footer={<><Button variant="ghost" onClick={onClose}>Close</Button><Button variant="primary" busy={save.pending} disabled={!valid} onClick={() => void save.mutate()}>{queue ? "Save queue" : "Create queue"}</Button></>}>
      <Field label="Name">{(id) => <input id={id} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Faith · English" autoComplete="off" />}</Field>
      <div className="grid-2">
        <Field label="Category">{(id) => (
          <select id={id} value={category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
            {category && !cats.some((c) => c.slug === category) && <option value={category}>{category}</option>}
          </select>
        )}</Field>
        <Field label="Language">{(id) => (
          <select id={id} value={form.language_code} onChange={(e) => setForm({ ...form, language_code: e.target.value })}>
            {(langs.length ? langs : [{ code: form.language_code, name: languageName(form.language_code) }]).map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
          </select>
        )}</Field>
      </div>
      <Field label="Time zone" hint={`Slot times are in this zone. Yours: ${browserTimeZone()}`}>{(id, d) => (
        <select id={id} aria-describedby={d} value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
          {timeZoneOptions(form.timezone).map((z) => <option key={z} value={z}>{z.replace(/_/g, " ")}</option>)}
        </select>
      )}</Field>
      <div className="field">
        <span className="field-label" id="slots-label">Daily slots</span>
        <ul className="chips-inline" aria-labelledby="slots-label">
          {form.slots.map((s) => (
            <li key={s} className="slot-chip">
              {s}
              <button type="button" className="slot-x" aria-label={`Remove ${s}`} disabled={form.slots.length <= 1} onClick={() => setForm({ ...form, slots: form.slots.filter((x) => x !== s) })}><Icon name="x" size={16} /></button>
            </li>
          ))}
        </ul>
        <div className="row">
          <input aria-label="New slot time" type="time" className="slot-input" value={newSlot} onChange={(e) => setNewSlot(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
          <Button size="sm" variant="secondary" icon="plus" onClick={add}>Add slot</Button>
        </div>
        {slotErr && <p className="field-error" role="alert">{slotErr}</p>}
      </div>
      <Field label="Minimum gap (minutes)" hint={`Posts are at least ${fmtGap(form.min_gap_minutes || 0)} apart.`}>{(id, d) => (
        <input id={id} aria-describedby={d} type="number" inputMode="numeric" min={0} max={10080} step={15} value={form.min_gap_minutes} onChange={(e) => setForm({ ...form, min_gap_minutes: Math.max(0, Math.min(10080, Number(e.target.value) || 0)) })} />
      )}</Field>
      <Switch label="Running" checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} hint="Off pauses the queue: nothing new is uploaded from it until you turn it back on." />
      {queue?.paused_at && <p className="note">Paused by ContentOps {dateTime(queue.paused_at)} (usually a channel that needs reconnecting).</p>}
    </Modal>
  );
}
